/* =========================================================================
   AROMATEX · Brand Scent Advisor
   app.js — Estado del quiz, render de pasos, progreso, lead + resultado
   ========================================================================= */

/* -------------------------------------------------------------------------
   CONFIGURACIÓN DE PASOS DEL QUIZ
   ------------------------------------------------------------------------- */
const STEPS = [
  {
    id: "companyName",
    type: "text",
    eyebrow: "Comencemos con tu marca",
    title: "¿Cómo se llama tu empresa?",
    placeholder: "Ej. Hotel Amara, Clínica Vitalis…",
    required: true,
  },
  {
    id: "industry",
    type: "single-icon",
    eyebrow: "Paso 2",
    title: "¿A qué se dedica tu empresa?",
    options: [
      { label: "Hotel", icon: "🏨" },
      { label: "Retail", icon: "🛍️" },
      { label: "Corporativo", icon: "🏢" },
      { label: "Hospital", icon: "🏥" },
      { label: "Clínica", icon: "🩺" },
      { label: "Gimnasio", icon: "🏋️" },
      { label: "Restaurante", icon: "🍽️" },
      { label: "Agencia automotriz", icon: "🚗" },
      { label: "Inmobiliaria", icon: "🏠" },
      { label: "Veterinaria", icon: "🐾" },
      { label: "Spa", icon: "🌿" },
      { label: "Otro", icon: "✨" },
    ],
    required: true,
  },
  {
    id: "spaces",
    type: "multi",
    eyebrow: "Paso 3",
    title: "¿Qué espacio deseas aromatizar?",
    subtitle: "Selecciona todos los que apliquen.",
    options: ["Lobby", "Recepción", "Oficinas", "Sala de ventas", "Showroom", "Baños", "Habitaciones", "Pasillos", "Sala de espera", "Restaurante"],
    required: true,
  },
  {
    id: "feelings",
    type: "multi",
    eyebrow: "Paso 4",
    title: "¿Qué quieres que sientan las personas cuando entren?",
    subtitle: "Máximo 3 respuestas.",
    max: 3,
    options: ["Exclusividad", "Tranquilidad", "Profesionalismo", "Confianza", "Energía", "Bienestar", "Calidez", "Concentración", "Frescura", "Cercanía", "Relajación", "Diversión"],
    required: true,
  },
  {
    id: "personality",
    type: "multi",
    eyebrow: "Paso 5",
    title: "¿Cómo describirías la personalidad de tu marca?",
    subtitle: "Selecciona hasta cinco. Aquí construimos el Brand DNA.",
    max: 5,
    options: ["Elegante", "Moderna", "Minimalista", "Natural", "Premium", "Innovadora", "Seria", "Amigable", "Sofisticada", "Fresca", "Disruptiva", "Juvenil", "Tecnológica", "Tradicional", "Masculina", "Femenina", "Neutra"],
    required: true,
  },
  {
    id: "mainGoal",
    type: "single",
    eyebrow: "Paso 6",
    title: "¿Cuál es tu objetivo principal?",
    subtitle: "Solo uno.",
    options: ["Diferenciar mi marca", "Mejorar la experiencia del cliente", "Crear un aroma exclusivo", "Incrementar permanencia", "Mejorar el ambiente laboral", "Reforzar mi identidad de marca", "Cubrir olores desagradables", "Otro"],
    required: true,
  },
  {
    id: "stayTime",
    type: "single",
    eyebrow: "Paso 7",
    title: "¿Tus visitantes permanecen normalmente?",
    subtitle: "Esto ayuda a definir la intensidad del aroma.",
    options: ["Menos de 10 minutos", "10-30 minutos", "30-60 minutos", "Más de una hora"],
    required: true,
  },
  {
    id: "clientTypes",
    type: "multi",
    eyebrow: "Paso 8",
    title: "¿Cómo describirías a tus clientes?",
    options: ["Empresarios", "Familias", "Turistas", "Ejecutivos", "Compradores premium", "Pacientes", "Estudiantes", "Mascotas", "Niños", "Público general"],
    required: false,
  },
  {
    id: "desiredIntensity",
    type: "single",
    eyebrow: "Paso 9",
    title: "¿Qué tan presente quieres que sea el aroma?",
    subtitle: "Esto también ayuda a definir la intensidad.",
    options: ["Muy discreto", "Natural", "Notorio", "Que se convierta en parte de la experiencia"],
    required: true,
  },
  {
    id: "branches",
    type: "single",
    eyebrow: "Paso 10",
    title: "¿Cuántas sucursales tienen?",
    options: ["1", "2-5", "6-20", "Más de 20"],
    required: true,
  },
  {
    id: "currentlyUsesScent",
    type: "single",
    eyebrow: "Ya casi terminamos…",
    title: "¿Actualmente utilizas aromatización?",
    options: ["No", "Sí, con otro proveedor", "Sí, con Aromatex"],
    required: true,
  },
  {
    id: "lead",
    type: "leadform",
    eyebrow: "Último paso",
    title: "¿Dónde te enviamos tu Perfil Olfativo?",
    required: true,
  },
];

/* -------------------------------------------------------------------------
   ESTADO
   ------------------------------------------------------------------------- */
const state = {
  step: 0,
  answers: {
    companyName: "",
    industry: null,
    spaces: [],
    feelings: [],
    personality: [],
    mainGoal: null,
    stayTime: null,
    clientTypes: [],
    desiredIntensity: null,
    branches: null,
    currentlyUsesScent: null,
    lead: { name: "", company: "", email: "", whatsapp: "" },
  },
  payload: null,
};

const root = document.getElementById("AromatexScentAdvisorApp");

// Configuración inyectada desde la sección de Shopify.
const LEAD_ENDPOINT = root?.dataset.leadEndpoint || "";
const CONSULTATION_URL = root?.dataset.consultationUrl || "/pages/contacto";
const STORAGE_KEY = "aromatex_brand_scent_advisor_leads_v1";

/* -------------------------------------------------------------------------
   HELPERS DE VALIDACIÓN
   ------------------------------------------------------------------------- */
function isStepValid(step) {
  const a = state.answers;
  switch (step.id) {
    case "companyName":
      return a.companyName.trim().length > 1;
    case "industry":
      return !!a.industry;
    case "spaces":
      return a.spaces.length > 0;
    case "feelings":
      return a.feelings.length > 0;
    case "personality":
      return a.personality.length > 0;
    case "mainGoal":
      return !!a.mainGoal;
    case "stayTime":
      return !!a.stayTime;
    case "clientTypes":
      return true; // opcional
    case "desiredIntensity":
      return !!a.desiredIntensity;
    case "branches":
      return !!a.branches;
    case "currentlyUsesScent":
      return !!a.currentlyUsesScent;
    case "lead": {
      const l = a.lead;
      const emailOk = /\S+@\S+\.\S+/.test(l.email);
      return l.name.trim().length > 1 && l.company.trim().length > 1 && emailOk;
    }
    default:
      return true;
  }
}

/* -------------------------------------------------------------------------
   RENDER: BARRA DE PROGRESO
   ------------------------------------------------------------------------- */
function renderProgress() {
  const currentStep = Math.min(state.step + 1, STEPS.length);
  const pct = (currentStep / STEPS.length) * 100;

  return `
    <div class="atx-progress-wrap" aria-label="Progreso del cuestionario">

      <div
        class="atx-progress-track"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="${Math.round(pct)}"
      >
        <div
          class="atx-progress-bar"
          style="width: ${pct}%;"
        ></div>
      </div>

      <div class="atx-progress-label">
        Paso ${currentStep} de ${STEPS.length}
      </div>

    </div>
  `;
}

function updateProgressBar() {
  const progressFill = document.querySelector(".progress-fill");

  if (!progressFill) return;

  const currentStep = Math.min(state.step + 1, STEPS.length);
  const pct = (currentStep / STEPS.length) * 100;

  progressFill.style.width = `${pct}%`;

  const progressTrack = document.querySelector(".progress-track");

  if (progressTrack) {
    progressTrack.setAttribute("aria-valuenow", Math.round(pct));
  }
}

/* Iconografía lineal para la pantalla de industria. Solo cambia presentación. */
function industryIcon(label) {
  const common = `viewBox="0 0 48 48" aria-hidden="true"`;
  const icons = {
    "Hotel": `<svg ${common}><path d="M7 34V18h34v16M10 18v-5h28v5M12 27h10v7H12zM26 27h10v7H26zM5 38h38"/></svg>`,
    "Retail": `<svg ${common}><path d="M13 17h22l3 25H10l3-25zM18 18v-5a6 6 0 0 1 12 0v5"/></svg>`,
    "Corporativo": `<svg ${common}><path d="M7 38h34M13 38V12h14v26M27 20h8v18M18 18h4M18 24h4M18 30h4M31 25h2M31 31h2"/></svg>`,
    "Hospital": `<svg ${common}><path d="M10 41V12h28v29M19 12V7h10v5M21 7h6M24 4v6M15 20h6M27 20h6M15 27h6M27 27h6M21 41V32h6v9"/></svg>`,
    "Clínica": `<svg ${common}><circle cx="24" cy="15" r="8"/><path d="M20 15h8M24 11v8M11 41v-6c0-7 5-12 13-12s13 5 13 12v6M17 41V31M31 41V31"/></svg>`,
    "Gimnasio": `<svg ${common}><path d="M8 20v8M13 17v14M35 17v14M40 20v8M13 24h22"/><circle cx="24" cy="11" r="4"/><path d="M24 15v9"/></svg>`,
    "Restaurante": `<svg ${common}><path d="M8 40h32M12 40a12 12 0 0 1 24 0M24 16v-5M19 11h10M11 21h26"/></svg>`,
    "Agencia automotriz": `<svg ${common}><path d="M8 31l4-10h24l4 10v8H8zM14 21l4-7h12l4 7M13 32h5M30 32h5"/><circle cx="15" cy="39" r="3"/><circle cx="33" cy="39" r="3"/></svg>`,
    "Inmobiliaria": `<svg ${common}><path d="M6 24L18 13l8 7 6-5 10 9v17H6zM13 41V29h10v12M29 28h7v7h-7z"/></svg>`,
    "Veterinaria": `<svg ${common}><path d="M18 25c-5 1-8 5-8 9 0 4 3 7 7 7 3 0 5-2 7-4 2 2 4 4 7 4 4 0 7-3 7-7 0-4-3-8-8-9-3-1-5 1-6 3-1-2-3-4-6-3z"/><circle cx="14" cy="16" r="4"/><circle cx="34" cy="16" r="4"/><circle cx="23" cy="10" r="4"/></svg>`,
    "Spa": `<svg ${common}><path d="M24 42c-7-7-12-13-12-20 0-7 5-12 12-12s12 5 12 12c0 7-5 13-12 20zM15 31c6-1 12-5 18-12M15 20c5 2 10 7 13 14"/></svg>`,
    "Otro": `<svg ${common}><path d="M7 18h34l-3 23H10zM11 18l4-8h18l4 8M16 26h16M16 32h16"/></svg>`,
  };
  return icons[label] || `<svg ${common}><circle cx="24" cy="24" r="16"/><path d="M24 16v16M16 24h16"/></svg>`;
}

/* -------------------------------------------------------------------------
   RENDER: CADA TIPO DE PASO
   ------------------------------------------------------------------------- */
function renderStepBody(step) {
  const a = state.answers;

  if (step.type === "text") {
    return `
      <input
        class="text-input"
        id="field-${step.id}"
        type="text"
        placeholder="${step.placeholder || ""}"
        value="${a[step.id] || ""}"
        autocomplete="off"
      />
    `;
  }

  if (step.type === "single-icon") {
    return `
      <div class="icon-grid">
        ${step.options
          .map(
            (opt) => `
          <button type="button" class="icon-card ${a.industry === opt.label ? "is-selected" : ""}" data-value="${opt.label}">
            <span class="icon-card__icon">${industryIcon(opt.label)}</span>
            <span class="icon-card__label">${opt.label}</span>
          </button>`
          )
          .join("")}
      </div>
    `;
  }

  if (step.type === "single") {
    return `
      <div class="option-list">
        ${step.options
          .map(
            (opt) => `
          <button type="button" class="option-pill ${a[step.id] === opt ? "is-selected" : ""}" data-value="${opt}">
            <span class="option-pill__radio"></span>
            <span>${opt}</span>
          </button>`
          )
          .join("")}
      </div>
    `;
  }

  if (step.type === "multi") {
    const selected = a[step.id] || [];
    const counter = step.max ? `<div class="counter">${selected.length}/${step.max} seleccionadas</div>` : "";
    return `
      ${counter}
      <div class="chip-grid">
        ${step.options
          .map(
            (opt) => `
          <button type="button" class="chip ${selected.includes(opt) ? "is-selected" : ""}" data-value="${opt}">
            ${opt}
          </button>`
          )
          .join("")}
      </div>
    `;
  }

  if (step.type === "leadform") {
    const l = a.lead;
    // Pre-llenar "Empresa" con el nombre capturado en el paso 1, pero
    // sincronizando también el estado real (no solo el valor visual del
    // input), para que la validación del botón "Continuar" lo reconozca
    // aunque el usuario no toque este campo.
    if (!l.company) l.company = a.companyName || "";
    return `
      <div class="lead-form">
        <label>Nombre
          <input class="text-input" id="lead-name" type="text" value="${l.name}" autocomplete="name" />
        </label>
        <label>Empresa
          <input class="text-input" id="lead-company" type="text" value="${l.company}" autocomplete="organization" />
        </label>
        <label>Correo
          <input class="text-input" id="lead-email" type="email" value="${l.email}" autocomplete="email" />
        </label>
        <label>WhatsApp
          <input class="text-input" id="lead-whatsapp" type="tel" value="${l.whatsapp}" autocomplete="tel" placeholder="+52 55 0000 0000" />
        </label>
        <p class="lead-note">Tus datos se usan únicamente para enviarte tu Perfil Olfativo de Marca y darte seguimiento comercial.</p>
      </div>
    `;
  }

  return "";
}

/* -------------------------------------------------------------------------
   RENDER: PANTALLA COMPLETA DE UN PASO
   ------------------------------------------------------------------------- */
function renderQuiz() {
  const step = STEPS[state.step];
  const isLast = state.step === STEPS.length - 1;
  // Importante: renderStepBody() puede tener efectos secundarios (p.ej.
  // pre-llenar "Empresa" con el nombre capturado antes). Por eso se calcula
  // ANTES de isStepValid(), para que la validación del botón ya vea el
  // estado actualizado y no quede un paso atrás.
  const bodyHtml = renderStepBody(step);
  const valid = isStepValid(step);

  root.innerHTML = `
    <div class="quiz-shell quiz-shell--${step.type}">
      ${renderProgress()}
      <div class="quiz-card" key="${step.id}">
        ${step.id === "companyName" ? `<p class="eyebrow">${step.eyebrow}</p>` : ""}
        <h1 class="step-title">${step.title}</h1>
        ${step.subtitle ? `<p class="step-subtitle">${step.subtitle}</p>` : ""}
        <div class="step-body">${bodyHtml}</div>
      </div>
      <div class="nav-row">
        <button class="btn btn-ghost" id="btn-back" ${state.step === 0 ? "disabled" : ""}>Atrás</button>
        <button class="btn btn-primary" id="btn-next" ${valid ? "" : "disabled"}>
          ${isLast ? "Ver mi Perfil Olfativo" : "Continuar"}
        </button>
      </div>
    </div>
  `;

  attachStepEvents(step);
  updateProgressBar();
  document.getElementById("btn-back").addEventListener("click", goBack);
  document.getElementById("btn-next").addEventListener("click", goNext);
}

function attachStepEvents(step) {
  const a = state.answers;

  if (step.type === "text") {
    const input = document.getElementById(`field-${step.id}`);
    input.addEventListener("input", (e) => {
      a[step.id] = e.target.value;
      refreshNextButton();
    });
    input.focus();
  }

  if (step.type === "single-icon") {
    document.querySelectorAll(".icon-card").forEach((btn) => {
      btn.addEventListener("click", () => {
        a.industry = btn.dataset.value;
        renderQuiz();
      });
    });
  }

  if (step.type === "single") {
    document.querySelectorAll(".option-pill").forEach((btn) => {
      btn.addEventListener("click", () => {
        a[step.id] = btn.dataset.value;
        renderQuiz();
      });
    });
  }

  if (step.type === "multi") {
    document.querySelectorAll(".chip").forEach((btn) => {
      btn.addEventListener("click", () => {
        const val = btn.dataset.value;
        const list = a[step.id];
        const idx = list.indexOf(val);
        if (idx >= 0) {
          list.splice(idx, 1);
        } else {
          if (step.max && list.length >= step.max) return; // límite alcanzado
          list.push(val);
        }
        renderQuiz();
      });
    });
  }

  if (step.type === "leadform") {
    ["name", "company", "email", "whatsapp"].forEach((field) => {
      const input = document.getElementById(`lead-${field}`);
      input.addEventListener("input", (e) => {
        a.lead[field] = e.target.value;
        refreshNextButton();
      });
    });
  }
}

function refreshNextButton() {
  const step = STEPS[state.step];
  const btn = document.getElementById("btn-next");
  if (btn) btn.disabled = !isStepValid(step);
}

function goBack() {
  if (state.step === 0) return;
  state.step -= 1;
  renderQuiz();
}

function goNext() {
  const step = STEPS[state.step];
  if (!isStepValid(step)) return;

  if (state.step === STEPS.length - 1) {
    submitQuiz();
    return;
  }
  state.step += 1;
  renderQuiz();
}

/* -------------------------------------------------------------------------
   SUBMIT: calcular resultado + render de pantalla de resultado
   ------------------------------------------------------------------------- */
async function submitQuiz() {
  state.payload = buildResultPayload(state.answers);

  // 1) Respaldo inmediato en el navegador.
  saveLeadLocally(state.payload);

  // 2) Envío automático al destino configurado (Google Sheets / webhook).
  // No bloqueamos la experiencia si el endpoint no responde.
  state.payload.meta = state.payload.meta || {};
  state.payload.meta.lead_delivery = await sendLead(state.payload);

  renderResult();
}

/* -------------------------------------------------------------------------
   RENDER: RESULTADO — "Perfil Olfativo de Marca"
   ------------------------------------------------------------------------- */
function starRow(intensity) {
  const map = { Suave: 1, Media: 2, Alta: 3 };
  const filled = map[intensity] || 2;
  let out = "";
  for (let i = 1; i <= 3; i++) out += `<span class="star ${i <= filled ? "is-on" : ""}">●</span>`;
  return out;
}

function aromaNotes(aromaName) {
  const info = typeof AROMA_CATALOG !== "undefined" ? AROMA_CATALOG[aromaName] : null;
  if (!info?.notes) return "";
  return [info.notes.salida, info.notes.corazon, info.notes.fondo].filter(Boolean).join(", ");
}

function renderResult() {
  const p = state.payload;
  const r = p.result;
  const clientName = p.lead?.name || "Cliente";

  root.innerHTML = `
    <div class="result-shell">
      <div class="result-hero">
        <div class="result-heading-row">
          <p class="eyebrow">${r.title}</p>
          <div class="client-chip">Cliente: ${clientName}</div>
        </div>
        <h1 class="result-brand">${p.answers.companyName || p.lead.company}</h1>

        <div class="pyramid">
          <div class="pyramid-row">
            <span class="pyramid-label">Personalidad</span>
            <span class="pyramid-value">${p.answers.personality.slice(0, 3).join(" · ")}</span>
          </div>
          <div class="pyramid-row">
            <span class="pyramid-label">Emoción</span>
            <span class="pyramid-value">${p.answers.feelings.join(" · ")}</span>
          </div>
          <div class="pyramid-row">
            <span class="pyramid-label">Objetivo</span>
            <span class="pyramid-value">${p.answers.mainGoal}</span>
          </div>
        </div>

        <div class="result-stats">
          <div class="stat">
            <span class="stat-value">${r.compatibility_percent}%</span>
            <span class="stat-label">Compatibilidad<br>aromática</span>
          </div>
          <div class="stat">
            <span class="stat-value">${ARCHETYPE_LABELS[r.brand_archetype.key] || r.brand_archetype.label}</span>
            <span class="stat-label">Arquetipo de<br>Marca</span>
          </div>
          <div class="stat">
            <span class="stat-value">${r.recommended_intensity}</span>
            <span class="stat-label">Intensidad<br>recomendada</span>
            <span class="stat-dots">${starRow(r.recommended_intensity)}</span>
          </div>
        </div>
      </div>

      ${
        r.custom_fragrance_flag
          ? `<div class="custom-banner">${r.custom_fragrance_message}</div>`
          : ""
      }

      <h2 class="section-title">Los aromas que recomendamos</h2>
      <div class="aroma-list">
        ${r.top_5_aromas
          .map(
            (aroma) => `
          <div class="aroma-card">
            <div class="aroma-rank">${aroma.rank}</div>
            <div class="aroma-info">
              <div class="aroma-name-row">
                <span class="aroma-family">${aroma.family}</span>
                <h3>${aroma.name}</h3>
              </div>
              ${aromaNotes(aroma.name) ? `<p class="aroma-notes">${aromaNotes(aroma.name)}</p>` : ""}
              <p class="aroma-reason">${aroma.reasoning}</p>
            </div>
          </div>`
          )
          .join("")}
      </div>

      ${
        r.also_recommended.length
          ? `<h2 class="section-title section-title--sub">También te recomendamos</h2>
             <div class="also-row">
               ${r.also_recommended.map((a) => `<span class="also-chip">${a.name}</span>`).join("")}
             </div>`
          : ""
      }

      <div class="lead-save-status ${p.meta?.lead_delivery?.ok ? "" : "is-warning"}">
        ${p.meta?.lead_delivery?.ok
          ? "Tu Perfil Olfativo fue registrado correctamente. Un especialista de Aromatex podrá dar seguimiento a tu solicitud."
          : "Tu Perfil Olfativo fue generado correctamente. Si deseas seguimiento inmediato, usa el botón de consultoría."}
      </div>

      <div class="cta-row">
        <button class="btn btn-primary" id="btn-schedule">Solicitar propuesta con Aromatex</button>
        <button class="btn btn-ghost" id="btn-print">Imprimir mi perfil</button>
        <button class="btn btn-ghost" id="btn-restart">Hacer otro perfil</button>
      </div>
    </div>
  `;

  document.getElementById("btn-schedule").addEventListener("click", () => {
    const separator = CONSULTATION_URL.includes("?") ? "&" : "?";
    const url = `${CONSULTATION_URL}${separator}empresa=${encodeURIComponent(p.answers.companyName || p.lead.company || "")}&origen=brand-scent-advisor`;
    window.location.href = url;
  });
  document.getElementById("btn-print").addEventListener("click", () => window.print());
  document.getElementById("btn-restart").addEventListener("click", restartQuiz);
}

/* -------------------------------------------------------------------------
   CAPTURA DE LEADS
   -------------------------------------------------------------------------
   - Guarda una copia local como respaldo en el navegador.
   - Envía automáticamente el payload al endpoint configurado en Shopify.
   - El endpoint recomendado incluido en este paquete es Google Apps Script,
     que agrega una fila a Google Sheets por cada cuestionario completado.
   ------------------------------------------------------------------------- */
function saveLeadLocally(payload) {
  try {
    const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    current.unshift(payload);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current.slice(0, 50)));
    console.info("Aromatex lead guardado localmente:", payload);
    return true;
  } catch (err) {
    console.warn("No fue posible guardar el respaldo local:", err);
    return false;
  }
}

async function sendLead(payload) {
  if (!LEAD_ENDPOINT) {
    console.warn("Endpoint de leads no configurado. Payload:", payload);
    return { ok: false, reason: "no-endpoint" };
  }

  try {
    // text/plain + no-cors evita el preflight CORS típico de Apps Script.
    // La respuesta es opaca, pero el POST sí llega al Web App publicado.
    await fetch(LEAD_ENDPOINT, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    console.info("Lead enviado al endpoint configurado.");
    return { ok: true, reason: "submitted" };
  } catch (err) {
    console.error("Error enviando lead:", err);
    return { ok: false, reason: "network-error" };
  }
}

function restartQuiz() {
  state.step = 0;
  state.answers = {
    companyName: "",
    industry: null,
    spaces: [],
    feelings: [],
    personality: [],
    mainGoal: null,
    stayTime: null,
    clientTypes: [],
    desiredIntensity: null,
    branches: null,
    currentlyUsesScent: null,
    lead: { name: "", company: "", email: "", whatsapp: "" },
  };
  state.payload = null;
  renderQuiz();
}

/* -------------------------------------------------------------------------
   INIT
   ------------------------------------------------------------------------- */
renderQuiz();
